import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import HeroSlider from "../components/HeroSlider";
import LiveNowHero from "../components/LiveNowHero";
import ContentRow from "../components/ContentRow";
import RisingThisWeek from "../components/RisingThisWeek";
import StoriesRail from "../components/StoriesRail";
import DownloadAppSection from "../components/DownloadAppSection";
import PushPromptBanner from "../components/PushPromptBanner";
import { SkeletonHero, SkeletonRow } from "../components/Skeleton";
import { useAuth } from "../context/AuthContext";

export default function Home() {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [forYou, setForYou] = useState([]);
  const [err, setErr] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    api.get("/videos/home").then((r) => setData(r.data)).catch((e) => setErr(String(e)));
    if (user) {
      api.get("/history").then((r) => setHistory(r.data)).catch(() => {});
      api.get("/videos/for-you?limit=12").then((r) => setForYou(r.data || [])).catch(() => {});
    }
  }, [user]);

  if (err) return <div className="min-h-screen flex items-center justify-center text-red-400">{err}</div>;
  if (!data) {
    return (
      <div className="min-h-screen">
        <SkeletonHero />
        <SkeletonRow variant="wide" />
        <SkeletonRow />
        <SkeletonRow />
      </div>
    );
  }

  const heroItem = data.live?.[0] || data.hero;
  const heroIsLive = !!data.live?.[0];

  return (
    <div data-testid="home-page" className="min-h-screen pb-24">
      {/* Mobile-only Push Notifications prompt banner. Dismissible for 7d. */}
      <PushPromptBanner />
      {/* Mobile-only floating "For You · Live" pill. Launches the vertical
          TikTok-style live feed. Kept at md:hidden so desktop keeps its
          existing hero flow. Positioned bottom-right so it doesn't collide
          with the top nav or the DownloadAppSection CTA at bottom. */}
      <Link
        to="/feed"
        data-testid="home-foryou-live-cta"
        className="md:hidden fixed z-40 bottom-24 right-4 flex items-center gap-2 rounded-full bg-red-600 pl-3 pr-4 py-2.5 shadow-2xl shadow-red-900/60 active:scale-95 transition-transform"
      >
        <span className="relative flex h-3 w-3">
          <span className="absolute inline-flex h-full w-full rounded-full bg-white opacity-60 animate-ping" />
          <span className="relative inline-flex h-3 w-3 rounded-full bg-white" />
        </span>
        <span className="text-white font-black text-xs uppercase tracking-widest">For You · Live</span>
      </Link>
      <section className="px-3 sm:px-6 md:px-0 pt-24 md:pt-28 pb-6 md:pb-10">
        <div data-testid="hero-banner" className="relative max-w-6xl md:max-w-none mx-auto md:mx-0 rounded-2xl md:rounded-none overflow-hidden bg-black">
          {/* Pre-rendered marketing banner */}
          <img
            src="/tapintv-hero-banner.png"
            alt="Tap IN TV Network — Stream it. Create it. Go Live. Own Your Channel."
            className="block w-full h-auto select-none"
            draggable={false}
          />

          {/* Clickable hotspots overlaid on the image. The image is square-ish
              (2000×2000) so percentages map cleanly across viewport sizes. */}

          {/* Top-left: Tap IN TV logo → /browse */}
          <Link
            to="/browse"
            aria-label="Browse Tap IN TV"
            data-testid="hero-hotspot-tv"
            className="absolute top-[10%] left-[28%] w-[18%] h-[30%]"
          />
          {/* Top-right: Tap IN Kids logo → /kids */}
          <Link
            to="/kids"
            aria-label="Enter Tap IN Kids"
            data-testid="hero-hotspot-kids"
            className="absolute top-[10%] left-[54%] w-[18%] h-[30%]"
          />

          {/* Bottom strip: 7 category tiles. Coordinates derived from the
              banner art — each tile is ~13.5% wide, vertical band 74–90%. */}
          {[
            { to: "/browse/films",    test: "hero-tile-films",     label: "Independent Films" },
            { to: "/browse/music",    test: "hero-tile-music",     label: "Music" },
            { to: "/browse/cooking",  test: "hero-tile-cooking",   label: "Cooking" },
            { to: "/browse/sports",   test: "hero-tile-sports",    label: "Sports" },
            { to: "/browse/gospel",   test: "hero-tile-gospel",    label: "Gospel" },
            { to: "/live",            test: "hero-tile-liveshows", label: "Live Shows" },
            { to: "/browse",          test: "hero-tile-more",      label: "And More" },
          ].map((tile, i) => (
            <Link
              key={tile.to + i}
              to={tile.to}
              aria-label={tile.label}
              data-testid={tile.test}
              className="absolute top-[73%] h-[16%]"
              style={{
                left:  `${3 + i * 13.5}%`,
                width: "13%",
              }}
            />
          ))}
        </div>

        {/* CTA buttons in a clean strip directly below the banner. */}
        <div className="max-w-6xl md:max-w-none mx-auto md:mx-0 mt-3 md:mt-4 flex gap-3 flex-wrap justify-center px-4">
          {user?.role !== "admin" && (
            <Link
              to="/subscribe"
              data-testid="cta-subscribe"
              className="bg-tv-live hover:bg-red-600 text-white font-bold px-6 py-3 rounded-full shadow-[0_10px_40px_-10px_rgba(239,68,68,0.7)] text-sm sm:text-base ring-2 ring-white/15"
            >Subscribe Now</Link>
          )}
          {user?.role !== "admin" && (
            <Link
              to="/apply-creator"
              data-testid="cta-become-creator"
              className="bg-tv-accent hover:bg-yellow-400 text-black font-bold px-6 py-3 rounded-full text-sm sm:text-base shadow-[0_10px_40px_-10px_rgba(250,204,21,0.7)] ring-2 ring-black/10"
            >Become a Creator</Link>
          )}
          {user?.role === "admin" && (
            <Link
              to="/admin"
              data-testid="cta-open-admin"
              className="bg-tv-accent hover:bg-yellow-400 text-black font-bold px-6 py-3 rounded-full text-sm sm:text-base shadow-[0_10px_40px_-10px_rgba(250,204,21,0.7)] ring-2 ring-black/10"
            >Open Admin Console</Link>
          )}
        </div>
      </section>

      <HeroSlider />

      {/* LIVE NOW hero — big cards above the fold when creators are
          actually live. Silently hides when empty. */}
      <div className="px-4 sm:px-8 md:px-12 pt-6">
        <LiveNowHero />
      </div>

      {/* Featured Creators — moved directly below hero per iter90 */}
      {data.creators?.length > 0 && (
        <section data-testid="row-creators" className="px-4 sm:px-8 md:px-12 py-8">
          <h2 className="font-display font-bold text-2xl sm:text-3xl text-white mb-5 tracking-tight">Featured Creators</h2>
          <div className="flex overflow-x-auto gap-5 hide-scrollbar pb-2">
            {data.creators.map((c) => (
              <Link
                to={`/creator/${c.user_id}`}
                key={c.user_id}
                data-testid={`creator-tile-${c.user_id}`}
                className="shrink-0 flex flex-col items-center text-center w-32 group"
              >
                <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-white/10 group-hover:border-tv-brand transition">
                  <img src={c.picture} alt={c.name} className="w-full h-full object-cover" />
                </div>
                <div className="mt-3 text-white font-semibold text-sm line-clamp-1">{c.name}</div>
                <div className="text-[10px] uppercase tracking-wider text-tv-accent mt-0.5">{c.role.replace("_", " ")}</div>
                <div className="text-xs text-white/50 mt-0.5">{c.follower_count.toLocaleString()} followers</div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <RisingThisWeek />

      {user && forYou.length > 0 && (
        <ContentRow title="For You" items={forYou} testId="row-for-you" />
      )}

      {data.live?.length > 0 && (
        <ContentRow title="Live Now" items={data.live} variant="wide" live testId="row-live" />
      )}

      {history.length > 0 && (
        <section data-testid="row-continue" className="px-3 sm:px-6 md:px-10 py-4 sm:py-6 tv-reveal">
          <h2 className="font-display font-bold text-xl sm:text-2xl md:text-3xl text-white tracking-tight mb-3 sm:mb-4">Continue Watching</h2>
          <div className="flex overflow-x-auto snap-x snap-mandatory gap-3 sm:gap-4 hide-scrollbar pb-2">
            {history.map((v) => {
              const pct = v.duration_sec ? (v.progress_sec / v.duration_sec) * 100 : 0;
              return (
                <Link key={v.id} to={`/watch/${v.id}`} className="tv-card snap-start shrink-0 w-[260px] sm:w-[300px] md:w-[380px] rounded-xl overflow-hidden relative" data-testid={`continue-${v.id}`}>
                  <div className="aspect-video bg-black"><img src={v.thumbnail_url} alt="" className="w-full h-full object-cover" /></div>
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black to-transparent p-3">
                    <div className="text-white text-sm font-semibold line-clamp-1">{v.title}</div>
                    <div className="tv-progress mt-2"><div style={{ width: `${Math.min(100, pct)}%`, background: "#EF4444" }} /></div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      <ContentRow title="Trending on Tap IN TV" items={data.trending} testId="row-trending" />

      {/* Top Stories — stretched full-width per iter90 */}
      <div className="w-full">
        <StoriesRail />
      </div>

      <ContentRow title="Recently Added" items={data.recent} testId="row-recent" />

      {data.fresh_social?.length > 0 && (
        <ContentRow title="Fresh From Your Socials" items={data.fresh_social} testId="row-fresh-social" />
      )}

      {data.rails?.map((rail) => (
        <ContentRow
          key={rail.category.slug}
          title={rail.category.name}
          items={rail.videos}
          testId={`row-cat-${rail.category.slug}`}
        />
      ))}

      <DownloadAppSection />

    </div>
  );
}
